/**
 * Workflow Step Manager Component
 *
 * Purpose: Manages workflow steps with CRUD operations, filtering, reordering, and validation.
 *          Steps are instances belonging to specific workflow definitions.
 *
 * Architecture: Table-based interface using MDTable component with modal forms
 *              for create/edit operations. Integrates with workflow service.
 *
 * Dependencies: React, Material-UI, MDTable, WorkflowService
 * Author: Development Team
 * Created: 2026-04-13
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Public as GlobalIcon,
  Business as TenantIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import MDTable from '../md-table';
import { getScopeChipColor } from '../../constants/mdm';
import { TableColumn } from '../../types/table';
import workflowService, {
  WorkflowStep,
  WorkflowStepFormData,
  WorkflowDefinition,
  StepTypeCapability,
} from '../../services/workflowService';
import aiAgentService, { AIAgent } from '../../services/aiAgentService';
import { TenantInfo } from '../../services/metadataService';
import { formatAuditTimestamp, formatAuditUser } from '../../utils/formatting';
import WorkflowStepModal from './modals/WorkflowStepModal';
import { RowActions, StatusChip } from '../common';
import { SecondaryButton } from '../common/ActionButtons';

// Component interfaces
interface WorkflowStepManagerProps {
  tenantContext: TenantInfo;
  canEdit: boolean;
  userPermissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
  filterWorkflowId?: string | null;
  fromWorkflowLabel?: string | null;
  onBackToDefinitions?: () => void;
}

/**
 * Workflow Step Manager Component
 */
const WorkflowStepManager: React.FC<WorkflowStepManagerProps> = ({
  tenantContext,
  canEdit,
  userPermissions: _userPermissions,
  filterWorkflowId,
  fromWorkflowLabel,
  onBackToDefinitions,
}) => {
  // State management
  const [data, setData] = useState<WorkflowStep[]>([]);
  const [workflowDefinitions, setWorkflowDefinitions] = useState<WorkflowDefinition[]>([]);
  const [availableAgents, setAvailableAgents] = useState<AIAgent[]>([]);
  const [stepTypeOptions, setStepTypeOptions] = useState<StepTypeCapability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedStep, setSelectedStep] = useState<WorkflowStep | undefined>();

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingStep, setDeletingStep] = useState<WorkflowStep | null>(null);

  // Filters
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>(filterWorkflowId || '');
  const [hideGlobal, setHideGlobal] = useState(false);

  // Sync filter with prop
  useEffect(() => {
    if (filterWorkflowId) {
      setSelectedWorkflowId(filterWorkflowId);
    }
  }, [filterWorkflowId]);

  // Load workflow definitions for dropdown
  const loadWorkflowDefinitions = useCallback(async () => {
    try {
      const response = await workflowService.getWorkflowDefinitions({
        tenant_id: tenantContext.tenant_id,
      });
      setWorkflowDefinitions(response || []);
    } catch (err) {
      console.error('Failed to load workflow definitions:', err);
    }
  }, [tenantContext.tenant_id]);

  // Load AI agents for assigned-agents dropdown
  const loadAgents = useCallback(async () => {
    try {
      const response = await aiAgentService.listAIAgents({
        tenant_id: tenantContext.tenant_id,
        status: 'active',
      });
      setAvailableAgents(response || []);
    } catch (err) {
      console.error('Failed to load AI agents:', err);
    }
  }, [tenantContext.tenant_id]);

  // Load the engine's step-type registry for the Step Type dropdown (ORCH-286 §5.4).
  // A failure here must not block the step list — the modal falls back to a
  // step-type-less (business lifecycle step) experience.
  const loadCapabilities = useCallback(async () => {
    try {
      const response = await workflowService.getCapabilities();
      setStepTypeOptions(response.step_types || []);
    } catch (err) {
      console.error('Failed to load engine capabilities:', err);
    }
  }, []);

  // Load step data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await workflowService.getWorkflowSteps({
        tenant_id: tenantContext.tenant_id,
        workflow_id: selectedWorkflowId || undefined,
      });
      setData(response || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflow steps');
    } finally {
      setLoading(false);
    }
  }, [tenantContext.tenant_id, selectedWorkflowId]);

  // Filter data based on hideGlobal setting
  const filteredData = useMemo(() => {
    if (hideGlobal) {
      return data.filter((item) => item.tenant_id !== 'GLOBAL');
    }
    return data;
  }, [data, hideGlobal]);

  // Drag-reorder needs rows for the same Workflow Definition to sit in
  // contiguous, sequence-ordered blocks — MDTable relies on that order both
  // for the group-boundary check and to draw the divider between groups.
  // Grid-internal sorting is disabled while reorderable, so this replaces
  // the `defaultSortModel` ordering that used to come from the column sort.
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const labelA = a.workflow_definition_label || a.workflow_id;
      const labelB = b.workflow_definition_label || b.workflow_id;
      if (labelA !== labelB) {
        return labelA.localeCompare(labelB);
      }
      return a.sequence_order - b.sequence_order;
    });
  }, [filteredData]);

  // Create custom toolbar filters
  const customToolbarFilters = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Hide GLOBAL Filter */}
        <FormControlLabel
          control={
            <Checkbox
              checked={hideGlobal}
              onChange={(e) => setHideGlobal(e.target.checked)}
              data-testid="hide-global-checkbox"
            />
          }
          label="Hide GLOBAL"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              color: 'text.secondary',
            },
          }}
        />

        {/* Workflow Definition Filter */}
        <Autocomplete
          data-testid="workflow-filter-combobox"
          size="small"
          disablePortal
          options={[
            { workflow_id: '', label: 'All Workflows' } as unknown as WorkflowDefinition,
            ...workflowDefinitions,
          ]}
          getOptionLabel={(option) =>
            (option as any).workflow_id === '' ? 'All Workflows' : option.label
          }
          isOptionEqualToValue={(option, value) => option.workflow_id === value.workflow_id}
          value={(() => {
            const allOption = {
              workflow_id: '',
              label: 'All Workflows',
            } as unknown as WorkflowDefinition;
            return (
              workflowDefinitions.find((w) => w.workflow_id === selectedWorkflowId) || allOption
            );
          })()}
          onChange={(_event, newValue) => {
            setSelectedWorkflowId(newValue?.workflow_id || '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Workflow"
              placeholder="Search workflows..."
              size="small"
            />
          )}
          sx={{ minWidth: 220 }}
          clearOnEscape
        />
      </Box>
    ),
    [hideGlobal, selectedWorkflowId, workflowDefinitions]
  );

  // Load data on component mount
  useEffect(() => {
    loadWorkflowDefinitions();
    loadAgents();
    loadCapabilities();
    loadData();
  }, [loadWorkflowDefinitions, loadAgents, loadCapabilities]);

  // Reload data when filter changes
  useEffect(() => {
    loadData();
  }, [selectedWorkflowId]);

  // MDTable fires one onRowReorder call per drop with no gap between calls,
  // so a rapid succession of drags would otherwise start fully overlapping
  // PUT+GET chains. reorderChainRef queues each invocation onto the previous
  // one so drops are persisted one at a time, in order, instead of firing
  // concurrent requests (ORCH-321).
  const reorderChainRef = useRef<Promise<void>>(Promise.resolve());

  // Drag-handle reorder handler, passed to MDTable's `onRowReorder`. Receives
  // the full row array in its new order; diffs each Workflow Definition
  // group against its pre-drag sequence to find which groups actually moved,
  // persists only those, and only refetches if something changed.
  const handleRowReorder = useCallback(
    (reorderedRows: WorkflowStep[]) => {
      const run = async () => {
        const byWorkflow = new Map<string, WorkflowStep[]>();
        reorderedRows.forEach((step) => {
          const group = byWorkflow.get(step.workflow_id) || [];
          group.push(step);
          byWorkflow.set(step.workflow_id, group);
        });

        let anyChanged = false;
        for (const [workflowId, steps] of byWorkflow.entries()) {
          const changed = steps.some((step, idx) => step.sequence_order !== idx + 1);
          if (!changed) continue;
          anyChanged = true;
          try {
            await workflowService.reorderWorkflowSteps({
              workflow_id: workflowId,
              tenant_id: steps[0].tenant_id,
              step_order: steps.map((step, idx) => ({
                step_id: step.step_id,
                sequence_order: idx + 1,
              })),
            });
          } catch (err) {
            console.error('Failed to reorder steps:', err);
          }
        }

        if (anyChanged) {
          await loadData();
        }
      };

      const chained = reorderChainRef.current.then(run);
      reorderChainRef.current = chained.catch(() => undefined);
      return chained;
    },
    [loadData]
  );

  // Resolve step reference IDs (global_step_id || step_id) to a list of step labels,
  // scoped to the same workflow so labels are unambiguous. Used by the Next Step
  // cell renderer below.
  const getLinkedStepLabels = useCallback(
    (row: WorkflowStep, refIds: string[] | undefined): string[] => {
      if (!refIds || refIds.length === 0) return [];
      const stepsInSameWorkflow = data.filter((s) => s.workflow_id === row.workflow_id);
      const labelByRefId = new Map<string, string>();
      stepsInSameWorkflow.forEach((s) => {
        const refId = s.global_step_id || s.step_id;
        labelByRefId.set(refId, s.label);
      });
      return refIds.map((id) => labelByRefId.get(id) || id);
    },
    [data]
  );

  const renderLinkedStepChips = (labels: string[]) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {labels.length > 0 ? (
        labels.map((l, index) => <Chip key={index} label={l} size="small" variant="outlined" />)
      ) : (
        <Typography variant="body2" color="textSecondary">
          {'—'}
        </Typography>
      )}
    </Box>
  );

  // Table columns configuration. Order is the one ordinal allowed ahead of the
  // record link (ORCH-225), so Name is the first data column; Workflow
  // Definition repeats the group header above the steps and is hidden by
  // default (still in the Columns menu).
  const columns: TableColumn[] = [
    {
      field: 'sequence_order',
      headerName: 'Order',
      width: 80,
      sortable: true,
      isOrderColumn: true,
      renderCell: (value: number) => <Typography variant="body2">{value}</Typography>,
    },
    {
      field: 'label',
      headerName: 'Name',
      width: 200,
      sortable: true,
      renderCell: (value: string) => <Typography variant="body2">{value}</Typography>,
    },
    {
      field: 'workflow_definition_label',
      headerName: 'Workflow Definition',
      width: 180,
      sortable: true,
      renderCell: (value: string, row: WorkflowStep) => {
        const wfLabel = value || row.workflow_id;
        return (
          <Typography variant="body2" color="textSecondary">
            {wfLabel}
          </Typography>
        );
      },
    },
    {
      field: 'tenant_id',
      headerName: 'Scope',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: WorkflowStep) => {
        const scopeInfo = (row as any).scope_info;
        let scopeLabel = '';
        let scopeIcon = <TenantIcon fontSize="small" />;

        if (scopeInfo) {
          scopeLabel = scopeInfo.scope;
          if (scopeLabel === 'Overridden') {
            scopeLabel = 'OVERRIDDEN';
          }
          scopeIcon =
            scopeLabel === 'GLOBAL' ? (
              <GlobalIcon fontSize="small" />
            ) : (
              <TenantIcon fontSize="small" />
            );
        } else {
          if (value === 'GLOBAL') {
            scopeLabel = 'GLOBAL';
            scopeIcon = <GlobalIcon fontSize="small" />;
          } else {
            scopeLabel = 'LOCAL';
            scopeIcon = <TenantIcon fontSize="small" />;
          }
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {scopeIcon}
            <Chip
              label={scopeLabel}
              size="small"
              color={getScopeChipColor(scopeLabel)}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 250,
      sortable: true,
      renderCell: (value: string) => (
        <Typography variant="body2" color="textSecondary">
          {value || '\u2014'}
        </Typography>
      ),
    },
    {
      field: 'next_steps',
      headerName: 'Next Step',
      width: 200,
      sortable: false,
      renderCell: (value: string[] | undefined, row: WorkflowStep) =>
        renderLinkedStepChips(getLinkedStepLabels(row, value)),
    },
    {
      field: 'status_flags',
      headerName: 'Status Flags',
      width: 200,
      sortable: false,
      renderCell: (value: string[]) => {
        const getStatusFlagColor = (
          flag: string
        ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
          switch (flag) {
            case 'Draft':
              return 'default';
            case 'In Progress':
              return 'info';
            case 'Approval':
              return 'warning';
            case 'Approved':
              return 'success';
            case 'Published':
              return 'primary';
            case 'Completed':
              return 'success';
            default:
              return 'default';
          }
        };

        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {value && value.length > 0 ? (
              value.map((flag, index) => (
                <Chip
                  key={index}
                  label={flag}
                  size="small"
                  variant="outlined"
                  color={getStatusFlagColor(flag)}
                />
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                {'\u2014'}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'assigned_agents',
      headerName: 'Assigned Agents',
      width: 220,
      sortable: false,
      renderCell: (value: string[] | undefined) => {
        const agentIds = value || [];
        if (agentIds.length === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              {'—'}
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {agentIds.map((agentId) => {
              const agent = availableAgents.find((a) => a.ai_agent_id === agentId);
              return (
                <Chip
                  key={agentId}
                  label={agent ? agent.label : agentId}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'updated_at',
      headerName: 'Modified Date',
      width: 140,
      sortable: true,
      renderCell: (value: string) => formatAuditTimestamp(value),
    },
    {
      field: 'updated_by',
      headerName: 'Modified By',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: WorkflowStep) => formatAuditUser(value, row.updated_by_user),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      renderCell: (value: string) => <StatusChip status={value} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (_value: any, row: WorkflowStep) => (
        <RowActions
          entityLabel="Workflow Step"
          rowLabel={row.label}
          testIdPrefix="workflow-step"
          rowTestId={`workflow-step-row-${row.step_id}`}
          onEdit={() => handleEdit(row)}
          onDelete={() => handleDeleteClick(row)}
          deleteDisabled={!canEdit}
        />
      ),
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setSelectedStep(undefined);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEdit = (step: WorkflowStep) => {
    setSelectedStep(step);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleRowClick = (row: WorkflowStep) => {
    handleEdit(row);
  };

  const handleSave = async (formData: WorkflowStepFormData) => {
    if (modalMode === 'create') {
      await workflowService.createWorkflowStep(formData);
    } else if (selectedStep) {
      // Strip workflow_id and tenant_id — not part of the update schema
      const { workflow_id, tenant_id, ...updateData } = formData;
      await workflowService.updateWorkflowStep(
        selectedStep.step_id,
        updateData,
        tenantContext.tenant_id
      );
    }
    loadData();
  };

  // Delete handlers
  const handleDeleteClick = (step: WorkflowStep) => {
    setDeletingStep(step);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingStep) return;
    try {
      await workflowService.deleteWorkflowStep(deletingStep.step_id);
      setDeleteDialogOpen(false);
      setDeletingStep(null);
      await loadData();
    } catch (err) {
      console.error('Failed to delete workflow step:', err);
    }
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeletingStep(null);
  };

  return (
    <Box>
      {/* Back to Workflow Definitions banner */}
      {fromWorkflowLabel && onBackToDefinitions && (
        <Box
          data-testid="back-to-workflow-definitions-banner"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.selected' },
          }}
          onClick={onBackToDefinitions}
        >
          <ArrowBackIcon fontSize="small" color="primary" />
          <Typography variant="body2" color="primary" fontWeight="medium">
            Back to Workflow Definitions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {'\u2014'} viewing steps for "{fromWorkflowLabel}"
          </Typography>
        </Box>
      )}

      {/* Data Table */}
      <Box>
        <MDTable
          mode="manual"
          manual={{
            columns,
            title: 'Workflow Steps',
            description: `${sortedData.length} workflow step(s) found`,
          }}
          data={sortedData.map((item) => ({ ...item, id: item.step_id }))}
          loading={loading}
          error={error}
          onRowClick={handleRowClick}
          onRefresh={loadData}
          onAddNew={canEdit ? handleCreate : undefined}
          showRefreshButton={true}
          showAddButton={canEdit}
          selectable={false}
          defaultSortModel={[{ field: 'workflow_definition_label', sort: 'asc' }]}
          sortable={true}
          filterable={true}
          reorderable={canEdit}
          groupByField="workflow_id"
          collapsibleGroups={true}
          groupLabelField="workflow_definition_label"
          reorderLabelField="label"
          onRowReorder={canEdit ? handleRowReorder : undefined}
          customToolbarFilters={customToolbarFilters}
          defaultColumnVisibility={{
            workflow_definition_label: false,
            description: false,
          }}
        />
      </Box>

      {/* Create/Edit Modal */}
      <WorkflowStepModal
        open={modalOpen}
        mode={modalMode}
        step={selectedStep}
        tenantContext={tenantContext}
        canEdit={canEdit}
        workflowDefinitions={workflowDefinitions}
        stepsInWorkflow={data}
        availableAgents={availableAgents}
        stepTypeOptions={stepTypeOptions}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onDelete={async (step) => {
          handleDeleteClick(step);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Workflow Step</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the workflow step "{deletingStep?.label}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleDeleteClose}>Cancel</SecondaryButton>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowStepManager;
